import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOPageLayout, { tokens } from '../components/SEOPageLayout';

const faqs = [
  {
    question: 'Is EvidentTrail read-only?',
    answer: 'Yes. EvidentTrail connects to your GitHub organization with read-only permissions. We never modify your code, workflows, CI pipelines, or branch protection settings. We only observe and record events.',
  },
  {
    question: 'Does EvidentTrail modify GitHub workflows?',
    answer: 'No. EvidentTrail does not add, modify, or remove any GitHub Actions, workflows, or CI pipeline configurations. Your development process remains exactly as it was before installation.',
  },
  {
    question: 'Does EvidentTrail track AI-generated code?',
    answer: 'No. EvidentTrail does not inspect source code or analyze generated output. We only record metadata indicating when AI tools were involved in a change and whether a human reviewed and approved it.',
  },
  {
    question: 'How does EvidentTrail help with GitHub Copilot or AI-assisted development audits?',
    answer: 'When AI tools assist in development, EvidentTrail captures metadata signals and links them to human approvals without storing prompts or generated content.',
  },
  {
    question: 'Is AI enforcement or risk scoring enabled?',
    answer: 'No. EvidentTrail\'s AI awareness is evidence-only. It does not enforce policies, score risk, or make automated decisions. Learn more about our AI-aware evidence capture.',
  },
  {
    question: 'Does EvidentTrail help with EU AI Act or ISO/IEC 42001 requirements?',
    answer: 'EvidentTrail supports organizations preparing for governance and audit requirements under frameworks such as the EU AI Act and ISO/IEC 42001 by providing verifiable evidence of human oversight over AI-assisted software changes. EvidentTrail records when AI tools were involved in a change and preserves system-generated evidence that a human reviewed and approved that change. EvidentTrail does not analyze AI models, prompts, or outputs, and does not provide AI risk scoring, enforcement, or certification.',
  },
  {
    question: 'Is EvidentTrail a replacement for Vanta or Drata?',
    answer: 'No. EvidentTrail focuses specifically on collecting evidence for software change control from GitHub. Tools like Vanta and Drata are broader compliance platforms that cover many control areas. EvidentTrail can complement these tools by providing deeper, more detailed evidence for change management controls.',
  },
  {
    question: 'Can auditors rely on EvidentTrail evidence?',
    answer: 'EvidentTrail evidence is system-generated from GitHub\'s API and exported with SHA-256 checksums. Auditors should evaluate evidence sources within the context of your control environment.',
  },
  {
    question: 'Who is EvidentTrail for?',
    answer: 'EvidentTrail is designed for teams that need change control evidence from GitHub, including engineering, security, and audit stakeholders.',
  },
  {
    question: 'What GitHub permissions does EvidentTrail need?',
    answer: 'EvidentTrail requests read-only access to: Pull Requests (to capture PR details and approvals), Checks (to capture CI/CD status), Contents (to read branch protection rules), and Metadata (to identify repositories). No write permissions are requested.',
  },
  {
    question: 'What is required to get started?',
    answer: 'Install the read-only GitHub App, select the repositories you want to track, and evidence collection begins for pull requests, approvals, branch protection snapshots, and exceptions.',
  },
  {
    question: 'What happens if I uninstall EvidentTrail?',
    answer: 'You can uninstall the EvidentTrail GitHub App at any time from your GitHub organization settings. There are no residual files, webhooks, or configurations left behind. Evidence collected before uninstallation is retained in EvidentTrail according to your data retention settings.',
  },
  {
    question: 'Does EvidentTrail access my source code?',
    answer: 'EvidentTrail reads metadata about repositories, branches, pull requests, and CI checks. It does not read or store source code content.',
  },
  {
    question: 'How is pricing structured?',
    answer: 'Pricing is plan-based with entitlements for tool access, retention, and usage quotas. Contact us for current plan details.',
  },
  {
    question: 'Can internal audit teams use EvidentTrail?',
    answer: 'Yes. EvidentTrail provides continuous visibility into software change controls. Internal auditors can use the timeline and drift detection features to monitor control effectiveness, sample changes for testing, and identify process gaps before external audits.',
  },
  {
    question: 'Does EvidentTrail work for regulated industries like finance or healthcare?',
    answer: 'EvidentTrail provides change control evidence that supports many regulatory frameworks. Contact us to discuss requirements for your environment.',
  },
];

// Generate JSON-LD FAQ structured data
const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function FAQPage() {
  return (
    <SEOPageLayout
      title="Frequently Asked Questions"
      description="Common questions about EvidentTrail: read-only access, GitHub permissions, auditor reliability, pricing, and how it compares to compliance platforms like Vanta."
    >
      {/* JSON-LD Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className={`${tokens.heading.h1} mb-6`}>
              Frequently Asked Questions
            </h1>
            <p className={tokens.text.large}>
              Common questions about EvidentTrail, GitHub integration, 
              and audit evidence collection.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className={tokens.card}>
                  <h2 className={`${tokens.heading.h3} mb-3`}>{faq.question}</h2>
                  <p className={tokens.text.base}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Still Have Questions?</h2>
            <p className={`${tokens.text.base} mb-8`}>
              We're happy to answer any questions about EvidentTrail, 
              your audit requirements, or how we can help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/#contact" className="btn btn-primary text-lg px-8 py-3">
                Contact Us
              </a>
              <Link to="/" className="text-slate-300 hover:text-white transition-colors">
                ← Back to Home
              </Link>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">Learn More</h3>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <Link to="/soc2-change-management-evidence" className="text-primary-400 hover:underline">
                  SOC 2 Evidence Collection →
                </Link>
                <Link to="/ai-assisted-development-evidence" className="text-primary-400 hover:underline">
                  AI-Assisted Development Evidence →
                </Link>
                <Link to="/outsourced-software-change-control" className="text-primary-400 hover:underline">
                  Outsourced Development Evidence →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
